// AudioSocket bot building blocks shared by the Templates and Containers pages
// (moved here from the old AudioSocket Bots page):
//   CloneModal   clone N bot LXCs from a template onto a server (+ CloneResult)
//   MachinesTab  every AudioSocket bot LXC across all servers (Containers → LXCs)

import React, { useState, useEffect, useMemo } from 'react'
import {
    Radio, FileText, Server, Headphones, FileAudio,
    Plus, Edit2, Trash2, X, Save, RefreshCw, Loader2,
    PhoneCall, Tag, Download, CheckCircle2, AlertCircle, Cpu,
    Play, Square, RotateCw, Mic, ChevronRight, ChevronDown, ChevronLeft, Undo2,
    Search, Boxes, Zap, PauseCircle, FileCog
} from 'lucide-react'
import { audiosocketBotsApi } from '../services/api'
import HealthBadge from './BotHealthBadge'
import StatCards from './StatCards'
import UpdateCodeModal, { isOutdated } from './BotCodeUpdate'
import BotEnvModal from './BotEnvModal'


export function CloneModal({ template, onClose, onDone }) {
    const [count, setCount] = useState(1)
    const [serverId, setServerId] = useState('')
    const [baseLxcId, setBaseLxcId] = useState(500)
    const [servers, setServers] = useState([])
    const [loadingServers, setLoadingServers] = useState(true)
    const [running, setRunning] = useState(false)
    const [err, setErr] = useState(null)
    const [job, setJob] = useState(null)         // currently-running job (sets the progress view)

    useEffect(() => {
        audiosocketBotsApi.listServers()
            .then(r => {
                const list = r.data.servers || []
                setServers(list)
                const ready = list.find(s => s.asterisk_status === 'installed')
                if (ready) setServerId(String(ready.id))
            })
            .catch(() => {})
            .finally(() => setLoadingServers(false))
    }, [])

    async function run() {
        setErr(null); setRunning(true)
        try {
            const r = await audiosocketBotsApi.clone(template.id, {
                count,
                server_id: parseInt(serverId),
                base_lxc_id: baseLxcId
            })
            // We now get { job_id } back immediately; switch to progress view.
            setJob({ id: r.data.job_id, progress: 0, step: 'starting…', status: 'running', results: [] })
        } catch (e) {
            setErr(e.response?.data?.error || e.message)
            setRunning(false)
        }
    }

    // Poll the job until it finishes.
    useEffect(() => {
        if (!job || job.status !== 'running') return
        let cancelled = false
        const tick = async () => {
            try {
                const r = await audiosocketBotsApi.getCloneJob(job.id)
                if (cancelled) return
                setJob(r.data.job)
                if (r.data.job.status === 'running') setTimeout(tick, 1500)
            } catch (e) {
                if (cancelled) return
                setTimeout(tick, 3000)
            }
        }
        const t = setTimeout(tick, 800)
        return () => { cancelled = true; clearTimeout(t) }
    }, [job?.id, job?.status])

    const readyServers = servers.filter(s => s.asterisk_status === 'installed')
    const noReady = !loadingServers && readyServers.length === 0

    // ----- Progress view -----
    if (job) {
        const done = job.status !== 'running'
        return (
            <div style={modalBg}>
                <div style={{ ...modalCard, maxWidth: 600 }}>
                    <h3 style={{ marginTop: 0 }}>
                        {job.status === 'done' ? 'Clone complete' :
                         job.status === 'failed' ? 'Clone failed' :
                         `Cloning ${template.name}…`}
                    </h3>

                    {/* progress bar */}
                    <div style={{ height: 12, background: '#0d1117', borderRadius: 6, overflow: 'hidden', marginTop: 8 }}>
                        <div style={{
                            height: '100%',
                            width: `${job.progress || 0}%`,
                            background: job.status === 'failed' ? '#dc2626' :
                                        job.status === 'done'   ? '#22c55e' : '#3b82f6',
                            transition: 'width 0.4s ease'
                        }} />
                    </div>
                    <div style={{ marginTop: 6, fontSize: 12, opacity: 0.8, fontFamily: 'monospace' }}>
                        {job.progress || 0}% — {job.step || ''}
                    </div>

                    {/* per-bot status table */}
                    {job.results && job.results.length > 0 && (
                        <table style={{ marginTop: 14, width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
                            <thead><tr style={{ opacity: 0.7, textAlign: 'left' }}>
                                <th style={{ padding: 4 }}>SIP</th>
                                <th style={{ padding: 4 }}>LXC</th>
                                <th style={{ padding: 4 }}>IP</th>
                                <th style={{ padding: 4 }}>Status</th>
                            </tr></thead>
                            <tbody>
                                {job.results.map(r => {
                                    const color = r.status === 'provisioned' ? '#22c55e' :
                                                  r.status === 'failed' || r.status === 'provision_failed' ? '#fca5a5' :
                                                  r.status === 'queued' ? '#9ca3af' : '#fbbf24'
                                    return (
                                        <tr key={r.sip_user} style={{ borderTop: '1px solid #2a2e36' }}>
                                            <td style={{ padding: 4, fontFamily: 'monospace' }}>{r.sip_user}</td>
                                            <td style={{ padding: 4, fontFamily: 'monospace' }}>{r.lxc_id || '—'}</td>
                                            <td style={{ padding: 4, fontFamily: 'monospace' }}>{r.lxc_ip || '—'}</td>
                                            <td style={{ padding: 4, color }}>{r.status}{r.error ? ' — ' + r.error.slice(0, 60) : ''}</td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    )}

                    {job.error && <div style={{ color: '#fca5a5', marginTop: 12, fontSize: 12 }}>Error: {job.error}</div>}

                    <p style={{ fontSize: 11, opacity: 0.6, marginTop: 12 }}>
                        Job runs in the background — you can close this and reopen the page; the job will still finish.
                    </p>

                    <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        {!done && <Btn icon={X} label="Hide" onClick={onClose} />}
                        {done && <Btn icon={X} label="Close" onClick={() => onDone(job)} primary />}
                    </div>
                </div>
            </div>
        )
    }

    // ----- Form view -----
    return (
        <div style={modalBg}>
            <div style={modalCard}>
                <h3 style={{ marginTop: 0 }}>Clone {template.name}</h3>
                <p style={{ fontSize: 13, opacity: 0.75 }}>
                    SIP range: <strong>{template.sip_user_start}–{template.sip_user_end}</strong>
                </p>
                <Field label="Server (must have Asterisk installed)">
                    {loadingServers ? <div style={{opacity:0.6, fontSize:12}}>loading…</div> :
                     noReady ? (
                        <div style={{ color: '#fca5a5', fontSize: 12, padding: 8, background: '#7f1d1d44', borderRadius: 4 }}>
                            No servers with Asterisk installed. Go to <strong>Servers</strong> tab → Add Server → Install Asterisk Stack first.
                        </div>
                     ) : (
                        <select value={serverId} onChange={e => setServerId(e.target.value)} style={inp}>
                            <option value="">— pick server —</option>
                            {servers.map(s => (
                                <option key={s.id} value={s.id} disabled={s.asterisk_status !== 'installed'}>
                                    {s.name} ({s.host_ip}) — {s.asterisk_status === 'installed' ? `LXC ${s.asterisk_lxc_id}` : `Asterisk ${s.asterisk_status}`}
                                </option>
                            ))}
                        </select>
                     )}
                </Field>
                <Field label="Number of bots to create">
                    <input type="number" min={1} max={20} value={count} onChange={e => setCount(parseInt(e.target.value) || 1)} style={inp} />
                </Field>
                <Field label="Base LXC id (next free above this is used)">
                    <input type="number" value={baseLxcId} onChange={e => setBaseLxcId(parseInt(e.target.value) || 500)} style={inp} />
                </Field>
                <p style={{ fontSize: 12, opacity: 0.6, marginTop: 12 }}>
                    Each LXC takes 3-5 min the first time (Vosk model download). Progress shows live below.
                </p>
                {err && <div style={{ color: '#fca5a5', marginTop: 12 }}>{err}</div>}
                <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <Btn icon={X} label="Cancel" onClick={onClose} />
                    <Btn icon={Plus} label="Clone" primary onClick={run} busy={running} />
                </div>
            </div>
        </div>
    )
}

export function CloneResult({ result, onClose }) {
    return (
        <div style={modalBg}>
            <div style={modalCard}>
                <h3 style={{ marginTop: 0 }}>Clone results</h3>
                <pre style={{ background: '#0d1117', padding: 10, borderRadius: 4, fontSize: 12, maxHeight: 400, overflow: 'auto' }}>
                    {JSON.stringify(result, null, 2)}
                </pre>
                <div style={{ marginTop: 16, textAlign: 'right' }}>
                    <Btn icon={X} label="Close" onClick={onClose} />
                </div>
            </div>
        </div>
    )
}

const modalBg = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
}
const modalCard = {
    background: '#1a1d23', border: '1px solid #2a2e36', color: '#e5e7eb',
    borderRadius: 8, padding: 20, width: '90%', maxWidth: 500
}

// ============================================================
// MACHINES
// ============================================================

const STOPPED_STATUSES = ['offline', 'deactivated', 'removed']   // = backend MACHINE_STOPPED_STATUSES
const PAGE_SIZE = 50

// All AudioSocket bot LXCs across every server. Used by the Containers page.
export function MachinesTab() {
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [expandedId, setExpandedId] = useState(null)   // open detail panel
    const [statuses, setStatuses] = useState({})         // { machine_id: {bot:{active,...}} }
    const [busy, setBusy] = useState({})                 // { machine_id: 'starting' | 'stopping' | ... }
    const [stateFilter, setStateFilter] = useState('')   // '' | running | stopped | attention
    const [serverFilter, setServerFilter] = useState('')
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(1)
    const [currentVersion, setCurrentVersion] = useState(null)
    const [updating, setUpdating] = useState(null)        // { mode: 'update'|'revert', bots } open in the code dialog
    const [envMachine, setEnvMachine] = useState(null)    // bot whose .env editor is open

    async function load() {
        setLoading(true)
        try {
            const r = await audiosocketBotsApi.listMachines()
            setItems(r.data.machines || [])
            setCurrentVersion(r.data.current_bot_version || null)
        } finally { setLoading(false) }
    }
    useEffect(() => { load() }, [])

    const isStopped = (m) => STOPPED_STATUSES.includes(m.status)
    const counts = useMemo(() => ({
        total: items.length,
        running: items.filter(m => !isStopped(m)).length,
        stopped: items.filter(isStopped).length,
        attention: items.filter(m => m.health?.alert).length,
        outdated: items.filter(m => m.health?.outdated).length
    }), [items])
    const serverOptions = useMemo(() => {
        const m = new Map()
        for (const x of items) if (x.server_id) m.set(x.server_id, x.server_name || `#${x.server_id}`)
        return [...m.entries()].sort((a, b) => String(a[1]).localeCompare(String(b[1])))
    }, [items])
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase()
        return items.filter(m =>
            (!serverFilter || String(m.server_id) === serverFilter) &&
            (stateFilter !== 'running' || !isStopped(m)) &&
            (stateFilter !== 'stopped' || isStopped(m)) &&
            (stateFilter !== 'attention' || m.health?.alert) &&
            (!q || [m.sip_user, m.lxc_id, m.lxc_ip, m.template_name, m.server_name]
                .some(v => String(v ?? '').toLowerCase().includes(q))))
    }, [items, search, serverFilter, stateFilter])
    useEffect(() => { setPage(1) }, [search, serverFilter, stateFilter])
    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
    const visible = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

    // Poll live service status every 10s — only for the rows on screen: each
    // check is an SSH round-trip to the bot's host, so polling every bot on
    // every server would mean hundreds of SSH calls per cycle.
    useEffect(() => {
        if (visible.length === 0) return
        let cancelled = false
        async function refreshAll() {
            const results = {}
            await Promise.all(visible.map(async m => {
                try {
                    const r = await audiosocketBotsApi.machineStatus(m.id)
                    results[m.id] = r.data
                } catch (e) {
                    results[m.id] = { error: e.response?.data?.error || e.message }
                }
            }))
            if (!cancelled) setStatuses(prev => ({ ...prev, ...results }))
        }
        refreshAll()
        const t = setInterval(refreshAll, 10000)
        return () => { cancelled = true; clearInterval(t) }
    }, [visible.map(m => m.id).join(',')])

    async function doAction(m, action) {
        const verbMap = { start: 'starting', stop: 'stopping', restart: 'restarting' }
        setBusy(b => ({ ...b, [m.id]: verbMap[action] }))
        try {
            if (action === 'start')   await audiosocketBotsApi.startMachine(m.id)
            if (action === 'stop')    await audiosocketBotsApi.stopMachine(m.id)
            if (action === 'restart') await audiosocketBotsApi.restartMachine(m.id)
            // refresh that bot's status after action
            const r = await audiosocketBotsApi.machineStatus(m.id)
            setStatuses(prev => ({ ...prev, [m.id]: r.data }))
        } catch (e) {
            alert(`${action} failed: ${e.response?.data?.error || e.message}`)
        } finally {
            setBusy(b => { const n = { ...b }; delete n[m.id]; return n })
        }
    }

    async function remove(m) {
        if (!window.confirm(`Delete bot ${m.sip_user} (LXC ${m.lxc_id} on ${m.server_name})?\n\nThis stops the bot, destroys its LXC, removes the DB row, and re-syncs Asterisk.`)) return
        setBusy(b => ({ ...b, [m.id]: 'deleting' }))
        try {
            await audiosocketBotsApi.deleteMachine(m.id)
            await load()
        } catch (e) {
            alert(`Delete failed: ${e.response?.data?.error || e.message}`)
        } finally {
            setBusy(b => { const n = { ...b }; delete n[m.id]; return n })
        }
    }

    const toggleState = (k) => setStateFilter(f => f === k ? '' : k)
    const outdatedFiltered = filtered.filter(m => isOutdated(m, currentVersion))

    return (
        <div>
            <StatCards cards={[
                { key: 'total', label: 'All LXCs', value: counts.total, icon: Boxes, color: '#60a5fa',
                  sub: `on ${serverOptions.length} server${serverOptions.length === 1 ? '' : 's'}`,
                  onClick: () => setStateFilter(''), active: !stateFilter },
                { key: 'running', label: 'Running', value: counts.running, icon: Zap, color: '#22c55e',
                  sub: counts.total ? `${Math.round(counts.running / counts.total * 100)}% of all` : null,
                  onClick: () => toggleState('running'), active: stateFilter === 'running' },
                { key: 'stopped', label: 'Stopped', value: counts.stopped, icon: PauseCircle, color: '#f59e0b',
                  sub: 'offline / deactivated', onClick: () => toggleState('stopped'), active: stateFilter === 'stopped' },
                { key: 'attention', label: 'Needs attention', value: counts.attention, icon: AlertCircle, color: '#ef4444',
                  sub: counts.outdated ? `${counts.outdated} on old bot version` : 'offline or stuck',
                  onClick: () => toggleState('attention'), active: stateFilter === 'attention',
                  title: 'Heartbeats stopped, or stuck unable to load config' }
            ]} />

            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <div style={{ position: 'relative', flex: '1 1 220px', maxWidth: 320 }}>
                    <Search size={14} style={{ position: 'absolute', left: 9, top: 9, opacity: 0.5 }} />
                    <input type="text" placeholder="Search SIP, LXC, IP, template…" value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ width: '100%', padding: '7px 10px 7px 30px', borderRadius: 6, fontSize: 13,
                                 background: 'var(--bg-input, var(--bg-card))', border: '1px solid var(--border-color)', color: 'inherit' }} />
                </div>
                <select value={serverFilter} onChange={e => setServerFilter(e.target.value)}
                    style={{ padding: '7px 10px', borderRadius: 6, fontSize: 13, background: 'var(--bg-input, var(--bg-card))',
                             border: '1px solid var(--border-color)', color: 'inherit' }}>
                    <option value="">All servers</option>
                    {serverOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                </select>
                <div style={{ flex: 1 }} />
                <div style={{ fontSize: 13, opacity: 0.75 }}>
                    {filtered.length === items.length ? `${items.length} LXC${items.length === 1 ? '' : 's'}` : `${filtered.length} of ${items.length}`}
                </div>
                {currentVersion && (
                    <Btn icon={Download} label={`Update outdated (${outdatedFiltered.length})`} primary
                        onClick={() => outdatedFiltered.length && setUpdating({ mode: 'update', bots: outdatedFiltered })}
                        busy={outdatedFiltered.length === 0} />
                )}
                <Btn icon={RefreshCw} label="Refresh" onClick={load} busy={loading} />
            </div>
            {loading && items.length === 0 ? <Row><Loader2 className="animate-spin" /></Row>
                     : items.length === 0
                     ? <Row><span style={{ opacity: 0.6 }}>No AudioSocket LXCs yet. Clone bots from a server's page or the Templates tab of AudioSocket Bots.</span></Row>
                     : filtered.length === 0
                     ? <Row><span style={{ opacity: 0.6 }}>No LXCs match these filters.</span></Row>
                     : (
                        <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ textAlign: 'left', opacity: 0.7 }}>
                                    <Th></Th>
                                    <Th>SIP</Th>
                                    <Th>Server</Th>
                                    <Th>LXC</Th>
                                    <Th>IP</Th>
                                    <Th>Template</Th>
                                    <Th>Bot</Th>
                                    <Th>STT</Th>
                                    <Th>Health</Th>
                                    <Th>Last seen</Th>
                                    <Th>Actions</Th>
                                </tr>
                            </thead>
                            <tbody>
                                {visible.map(m => {
                                    const st = statuses[m.id]
                                    const botActive = st?.bot?.active
                                    const sttActive = st?.bot?.stt_active
                                    const busyState = busy[m.id]
                                    const expanded = expandedId === m.id
                                    const lastSeen = m.last_seen_at ? new Date(m.last_seen_at).toLocaleString() : '—'
                                    return (
                                        <React.Fragment key={m.id}>
                                            <tr style={{ borderTop: '1px solid #2a2e36' }}>
                                                <Td>
                                                    <button onClick={() => setExpandedId(expanded ? null : m.id)}
                                                        style={{ background:'transparent', border:'none', color:'#9ca3af', cursor:'pointer' }}>
                                                        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                    </button>
                                                </Td>
                                                <Td><strong style={{ fontFamily:'monospace' }}>{m.sip_user}</strong></Td>
                                                <Td>{m.server_name || `#${m.server_id}`}</Td>
                                                <Td style={{ fontFamily:'monospace' }}>{m.lxc_id}</Td>
                                                <Td style={{ fontFamily:'monospace' }}>{m.lxc_ip}</Td>
                                                <Td style={{ fontSize: 12, opacity: 0.85 }}>{m.template_name || (m.template_id ? `#${m.template_id}` : '—')}</Td>
                                                <Td><StatusDot active={botActive} /></Td>
                                                <Td><StatusDot active={sttActive} /></Td>
                                                <Td><HealthBadge health={m.health} version={m.version} /></Td>
                                                <Td style={{ fontSize:11, opacity:0.75 }}>{lastSeen}</Td>
                                                <Td style={{ whiteSpace: 'nowrap' }}>
                                                    {botActive
                                                        ? <Btn icon={Square} label="Stop" small onClick={() => doAction(m, 'stop')} busy={busyState === 'stopping'} />
                                                        : <Btn icon={Play} label="Start" small primary onClick={() => doAction(m, 'start')} busy={busyState === 'starting'} />}
                                                    <Btn icon={RotateCw} label="Restart" small onClick={() => doAction(m, 'restart')} busy={busyState === 'restarting'} />
                                                    <Btn icon={Download} label="Update" small onClick={() => setUpdating({ mode: 'update', bots: [m] })} />
                                                    <Btn icon={Undo2} label="Revert" small onClick={() => setUpdating({ mode: 'revert', bots: [m] })} />
                                                    <Btn icon={FileCog} label=".env" small onClick={() => setEnvMachine(m)} />
                                                    <Btn icon={Trash2} label="Delete" small danger onClick={() => remove(m)} busy={busyState === 'deleting'} />
                                                </Td>
                                            </tr>
                                            {expanded && (
                                                <tr><td colSpan={11} style={{ padding: 0, background: '#0d1117' }}>
                                                    <MachineDetail machine={m} status={st} />
                                                </td></tr>
                                            )}
                                        </React.Fragment>
                                    )
                                })}
                            </tbody>
                        </table>
                        </div>
                     )}
            {updating && (
                <UpdateCodeModal mode={updating.mode} bots={updating.bots} currentVersion={currentVersion}
                    onClose={() => setUpdating(null)} onDone={() => load()} />
            )}
            {envMachine && <BotEnvModal machine={envMachine} onClose={() => setEnvMachine(null)} />}
            {pageCount > 1 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13 }}>
                    <Btn icon={ChevronLeft} label="Prev" small onClick={() => setPage(p => Math.max(1, p - 1))} />
                    <span style={{ minWidth: 60, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{page} / {pageCount}</span>
                    <Btn icon={ChevronRight} label="Next" small onClick={() => setPage(p => Math.min(pageCount, p + 1))} />
                </div>
            )}
        </div>
    )
}

function StatusDot({ active }) {
    if (active === undefined) return <span style={{ fontSize: 11, opacity: 0.4 }}>—</span>
    return (
        <span style={{
            display:'inline-block', width:9, height:9, borderRadius:'50%',
            background: active ? '#22c55e' : '#dc2626',
            boxShadow: active ? '0 0 4px #22c55e' : 'none'
        }} title={active ? 'active' : 'inactive'} />
    )
}

function MachineDetail({ machine, status }) {
    const [log, setLog] = useState('')
    const [logWhich, setLogWhich] = useState('bot')
    const [recordings, setRecordings] = useState([])
    const [recLoading, setRecLoading] = useState(false)
    const [logLoading, setLogLoading] = useState(false)
    const [transcript, setTranscript] = useState(null)  // { filename, text }

    async function loadLog() {
        setLogLoading(true)
        try {
            const r = await audiosocketBotsApi.machineLog(machine.id, { which: logWhich, lines: 200 })
            setLog(r.data.log || '')
        } catch (e) {
            setLog(`Failed to fetch log: ${e.response?.data?.error || e.message}`)
        } finally { setLogLoading(false) }
    }
    async function loadRec() {
        setRecLoading(true)
        try {
            const r = await audiosocketBotsApi.machineRecordings(machine.id)
            setRecordings(r.data.recordings || [])
        } catch (e) {
            setRecordings([])
        } finally { setRecLoading(false) }
    }
    async function showTranscript(rec) {
        const txt = rec.filename.replace(/\.wav$/, '.txt')
        try {
            const r = await audiosocketBotsApi.machineTranscript(machine.id, txt)
            setTranscript({ filename: txt, text: r.data.text || '(empty)' })
        } catch (e) {
            setTranscript({ filename: txt, text: `(error: ${e.response?.data?.error || e.message})` })
        }
    }
    useEffect(() => { loadLog(); loadRec() }, [machine.id, logWhich])
    // refresh log every 5s while panel is open
    useEffect(() => {
        const t = setInterval(loadLog, 5000)
        return () => clearInterval(t)
    }, [machine.id, logWhich])

    return (
        <div style={{ padding: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, color: '#e5e7eb' }}>
            {/* Log */}
            <div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 6 }}>
                    <strong style={{ fontSize: 12, opacity: 0.8 }}>Live log ({logWhich})</strong>
                    <div style={{ display:'flex', gap: 4 }}>
                        <button onClick={() => setLogWhich('bot')}
                            style={tabBtn(logWhich === 'bot')}>bot</button>
                        <button onClick={() => setLogWhich('stt')}
                            style={tabBtn(logWhich === 'stt')}>stt</button>
                        <Btn icon={RefreshCw} label="" small onClick={loadLog} busy={logLoading} />
                    </div>
                </div>
                <pre style={{
                    background: '#000', color: '#86efac', padding: 8, fontSize: 11,
                    borderRadius: 4, height: 260, overflow: 'auto', margin: 0, fontFamily:'monospace'
                }}>{log || '(no log yet)'}</pre>
                {status?.bot && (
                    <div style={{ fontSize: 11, opacity: 0.7, marginTop: 6 }}>
                        uptime: {status.bot.uptime} · listening: {status.bot.listening ? 'yes (:9999)' : 'no'}
                    </div>
                )}
            </div>

            {/* Recordings */}
            <div>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 6 }}>
                    <strong style={{ fontSize: 12, opacity: 0.8 }}>Recordings ({recordings.length})</strong>
                    <Btn icon={RefreshCw} label="" small onClick={loadRec} busy={recLoading} />
                </div>
                <div style={{ background: '#000', borderRadius: 4, height: 260, overflow: 'auto', padding: 8 }}>
                    {recordings.length === 0
                        ? <div style={{ opacity: 0.5, fontSize: 12 }}>no recordings yet</div>
                        : recordings.map(r => (
                            <div key={r.filename} style={{ borderBottom: '1px solid #2a2e36', padding: '6px 0' }}>
                                <div style={{ fontFamily: 'monospace', fontSize: 11 }}>{r.filename}</div>
                                <div style={{ fontSize: 10, opacity: 0.6 }}>
                                    {r.modified ? new Date(r.modified).toLocaleString() : '—'} · {(r.size/1024).toFixed(1)} KB
                                    {r.has_transcript && <span style={{ color: '#22c55e', marginLeft: 6 }}> · transcript</span>}
                                </div>
                                <div style={{ marginTop: 4 }}>
                                    <audio controls preload="none" src={audiosocketBotsApi.machineRecordingUrl(machine.id, r.filename)}
                                        style={{ height: 28, maxWidth: '100%' }} />
                                    {r.has_transcript && (
                                        <button onClick={() => showTranscript(r)}
                                            style={{ marginLeft: 6, background:'transparent', color:'#3b82f6',
                                                     border:'1px solid #3b82f6', borderRadius:3, fontSize:11, padding:'2px 6px', cursor:'pointer' }}>
                                            transcript
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                </div>
            </div>

            {transcript && (
                <div style={modalBg} onClick={() => setTranscript(null)}>
                    <div style={{ ...modalCard, maxWidth: 700 }} onClick={e => e.stopPropagation()}>
                        <h3 style={{ marginTop: 0, fontSize: 14, fontFamily: 'monospace' }}>{transcript.filename}</h3>
                        <pre style={{ background:'#0d1117', padding: 10, borderRadius: 4, fontSize: 12,
                                      maxHeight: 400, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                            {transcript.text}
                        </pre>
                        <div style={{ marginTop: 12, textAlign:'right' }}>
                            <Btn icon={X} label="Close" onClick={() => setTranscript(null)} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

const tabBtn = (active) => ({
    background: active ? '#3b82f6' : 'transparent',
    color: active ? 'white' : '#9ca3af',
    border: '1px solid ' + (active ? '#3b82f6' : '#2a2e36'),
    borderRadius: 3,
    fontSize: 10,
    padding: '2px 8px',
    cursor: 'pointer'
})

// ============================================================
// SERVERS
// ============================================================

// ============================================================
// shared bits
// ============================================================

const inp = {
    padding: '6px 10px', borderRadius: 4,
    border: '1px solid #2a2e36', background: '#0d1117',
    color: '#e5e7eb', fontSize: 13, width: '100%'
}

function Th({ children }) { return <th style={{ padding:'8px 8px', fontWeight:600 }}>{children}</th> }
function Td({ children, ...rest }) { return <td style={{ padding:'8px 8px', ...rest.style }}>{children}</td> }

function Row({ children }) {
    return <div style={{ background:'#1a1d23', border:'1px solid #2a2e36', color:'#e5e7eb', padding:16, borderRadius:8 }}>{children}</div>
}

function Section({ title, children }) {
    return (
        <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, opacity: 0.6, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{title}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>{children}</div>
        </div>
    )
}

function Field({ label, children }) {
    return (
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, opacity: 0.85 }}>
            {label}
            {children}
        </label>
    )
}

function Btn({ icon: I, label, onClick, primary, danger, small, busy }) {
    const bg = busy ? '#2a2e36' : (danger ? '#dc2626' : (primary ? '#3b82f6' : 'transparent'))
    const fg = primary || danger ? 'white' : '#3b82f6'
    const border = primary || danger ? 'none' : '1px solid #3b82f6'
    return (
        <button onClick={onClick} disabled={busy}
            style={{
                background: bg, color: fg, border, borderRadius: 4,
                padding: small ? '4px 8px' : '6px 12px',
                cursor: busy ? 'not-allowed' : 'pointer',
                marginLeft: small ? 4 : 0,
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: small ? 12 : 13
            }}>
            {busy ? <Loader2 className="animate-spin" size={14} /> : I && <I size={14} />}
            {label}
        </button>
    )
}
